/**
 * A labelled field, for a dialog's form.
 *
 * THIS FILE USED TO HOLD `Column` AND `Section` TOO, the reading column and titled group the
 * tabbed Settings page was built from. Settings v2 is a nav of panes built from cards
 * (`SettingCard`, `SettingRow` in `ui.tsx`), so both went with the tabs; the connection dialog
 * is what still wants a label over a control.
 */

import type { ReactNode } from "react";

/**
 * A control with its name over it.
 *
 * The `<label>` WRAPS the control rather than pointing at it with `htmlFor`, which is what
 * makes the association real without every call site having to invent an id - and it is
 * why each of these holds exactly one input.
 *
 * THE HINT AND THE COUNTER SIT OUTSIDE THE LABEL, and that is not tidiness. Inside it they
 * become part of the control's accessible NAME, and a one-word field starts announcing
 * itself as "What you do 156/1000". This used to be a second component, `InlineField`,
 * invented for that one problem on the one field that has a counter; it was the same
 * insight and it only ever needed to be this.
 */
export function Field({
  label,
  hint,
  count,
  children,
}: {
  label: string;
  hint?: ReactNode;
  /** A character count or similar, set against the right edge under the control. */
  count?: ReactNode;
  children: ReactNode;
}) {
  return (
    <div>
      <label className="block">
        <span className="mb-1.5 block text-sm font-medium text-ink">{label}</span>
        {children}
      </label>
      {(hint || count) && (
        <div className="mt-1.5 flex items-start justify-between gap-4 text-sm text-ink-faint">
          <span>{hint}</span>
          {count && <span className="flex-none nums">{count}</span>}
        </div>
      )}
    </div>
  );
}
